// How a customer came to onGuard (ENTRAID-337). One source for the allowed
// values and labels, used by the backend (validation, read-time fallback, the
// migration script) and the frontend (create form, customer details).
//
// `channel` answers "how did this customer arrive". `productCode` keeps one
// meaning only: the AWS Marketplace product code, written by the Marketplace
// registration path and never by a manual create.

export const CHANNELS = Object.freeze(['marketplace', 'internal', 'pilot', 'partner']);

export const CHANNEL_LABELS = Object.freeze({
  marketplace: 'AWS Marketplace',
  internal: 'Internal (IPC Global)',
  pilot: 'Pilot',
  partner: 'Partner',
});

/** What an operator may choose on the create form. Marketplace is never picked by hand. */
export const MANUAL_CHANNELS = Object.freeze(['internal', 'pilot', 'partner']);

export const DEFAULT_MANUAL_CHANNEL = 'internal';

// The marker values the old single productCode field held for non-Marketplace
// customers, and the channel each one means.
export const LEGACY_MARKER_CHANNELS = Object.freeze({
  internal: 'internal',
  'comp-trial': 'pilot',
  partner: 'partner',
});

export const isChannel = (value) => typeof value === 'string' && CHANNELS.includes(value);
export const isManualChannel = (value) => typeof value === 'string' && MANUAL_CHANNELS.includes(value);

export function channelLabel(channel) {
  return CHANNEL_LABELS[channel] ?? 'Unknown';
}

/** True when productCode holds an old non-Marketplace marker, not a real product code. */
export const isLegacyMarker = (productCode) =>
  typeof productCode === 'string' && Object.hasOwn(LEGACY_MARKER_CHANNELS, productCode);

/**
 * The channel of a customer record. A stored, valid `channel` always wins.
 * For records written before ENTRAID-337 it is derived, in this order:
 * a Marketplace record (it has a licenseArn, or came through register with
 * source "marketplace") is "marketplace"; otherwise the legacy productCode
 * marker maps (internal, comp-trial -> pilot, partner); otherwise a record
 * flagged `internal: true` is "internal". Returns null when nothing is known.
 *
 * @param {object|null|undefined} customer
 * @returns {'marketplace'|'internal'|'pilot'|'partner'|null}
 */
export function channelOf(customer) {
  if (!customer || typeof customer !== 'object') return null;
  if (isChannel(customer.channel)) return customer.channel;
  if (customer.licenseArn || customer.source === 'marketplace') return 'marketplace';
  if (isLegacyMarker(customer.productCode)) return LEGACY_MARKER_CHANNELS[customer.productCode];
  if (customer.internal === true) return 'internal';
  return null;
}

/**
 * The real AWS Marketplace product code of a record, or undefined: a legacy
 * marker is never one.
 */
export function marketplaceProductCode(customer) {
  const code = customer?.productCode;
  if (typeof code !== 'string' || code === '' || isLegacyMarker(code)) return undefined;
  return code;
}
